import React, { useEffect, useRef, useState, useCallback } from "react";
import { Avatar } from "@/components/common/Avatar";
import { Search, Send, Loader2, Ban, ShieldCheck, Archive, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { dmApi } from "@/lib/dmApi";
import { getSocket } from "@/lib/socket";
import { useToast } from "@/contexts/ToastContext";

const ACCENT = "var(--accent-primary)";
const MAX = 500;
const TOKEN = () => localStorage.getItem("samtwo_platform_token") || localStorage.getItem("samtwo_token");
const timeStr = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

// SAMTWO's inbox: Don / General tabs, thread list + conversation, reply, block.
// Used inside the admin Audience > Inbox sub-tab and full-page at /inbox.
export const DmInbox = () => {
  const { i18n } = useTranslation();
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [data, setData] = useState({ threads: [], total_unread: 0 });
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // conversation {subscriber, messages, is_blocked, thread_id}
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [tr, setTr] = useState({}); // messageId -> { text, show, loading }
  const endRef = useRef(null);

  // Artist reads received (subscriber) messages in English, on-demand only.
  const toggleTranslate = async (m) => {
    const cur = tr[m.id];
    if (cur && cur.text != null) { setTr((p) => ({ ...p, [m.id]: { ...cur, show: !cur.show } })); return; }
    setTr((p) => ({ ...p, [m.id]: { loading: true, show: false } }));
    try {
      const { data } = await dmApi.translate(m.id, i18n.language);
      setTr((p) => ({ ...p, [m.id]: { text: data.body, show: true, loading: false } }));
    } catch {
      setTr((p) => { const n = { ...p }; delete n[m.id]; return n; });
      toast.error("Could not translate message");
    }
  };

  const loadInbox = useCallback(async (s = "") => {
    setLoading(true);
    try { const { data } = await dmApi.inbox(s); setData(data); } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadInbox(); }, [loadInbox]);

  // Real-time: join shared inbox room. Refresh the thread list on new-dm, and
  // live-append the incoming message into the open conversation off the same
  // socket payload (no conversation refetch).
  useEffect(() => {
    const socket = getSocket();
    const ident = () => socket.emit("identify_inbox", { token: TOKEN() });
    ident();
    socket.on("connect", ident);
    const onNew = (p) => {
      loadInbox(search);
      const m = p && p.message;
      setActive((a) => {
        if (!a || !m || a.subscriber?.id !== p.subscriber_id) return a;
        if (a.messages.some((x) => x.id === m.id)) return a;
        return { ...a, messages: [...a.messages, m] };
      });
    };
    socket.on("new-dm", onNew);
    return () => { socket.off("new-dm", onNew); socket.off("connect", ident); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadInbox, search]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [active?.messages]);

  const openThread = async (subscriberId, silent = false) => {
    try {
      const { data } = await dmApi.conversation(subscriberId);
      setActive(data);
      if (!silent) { await dmApi.markRead(data.thread_id); loadInbox(search); }
    } catch {}
  };

  const send = async () => {
    const body = reply.trim();
    if (!body || !active || sending) return;
    setSending(true);
    try {
      const { data } = await dmApi.reply(active.subscriber.id, body);
      setActive((a) => ({ ...a, messages: [...a.messages, data] }));
      setReply("");
      loadInbox(search);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    finally { setSending(false); }
  };

  const toggleBlock = async () => {
    if (!active) return;
    const next = !active.is_blocked;
    try {
      await dmApi.block(active.subscriber.id, next);
      setActive((a) => ({ ...a, is_blocked: next }));
      toast.success(next ? "Subscriber blocked from DMs" : "Subscriber unblocked");
    } catch { toast.error("Could not update block"); }
  };

  const archive = async () => {
    if (!active) return;
    try { await dmApi.archive(active.thread_id); setActive(null); loadInbox(search); toast.success("Conversation archived"); }
    catch { toast.error("Could not archive"); }
  };

  const threads = data.threads || [];

  return (
    <div data-testid="dm-inbox" className="flex gap-4 h-[70vh] min-h-[480px]">
      {/* Thread list: full-width on mobile, hidden once a thread is open; fixed rail on desktop */}
      <div className={`${active ? "hidden md:flex" : "flex"} flex-col w-full md:w-[300px] flex-shrink-0 rounded-2xl overflow-hidden`} style={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)" }}>
        <div className="p-3 border-b" style={{ borderColor: "var(--border-color)" }}>
          <div className="flex items-center gap-2 px-3 py-2 rounded-full" style={{ background: "var(--bg-subtle)" }}>
            <Search size={15} style={{ color: "var(--text-secondary)" }} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadInbox(search)}
              placeholder="Search messages" data-testid="dm-search" className="bg-transparent outline-none text-sm w-full" style={{ color: "var(--text-primary)" }} />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? <div className="flex justify-center pt-8"><Loader2 className="animate-spin" style={{ color: ACCENT }} /></div>
            : threads.length === 0 ? <p className="text-xs text-center pt-8" style={{ color: "var(--text-secondary)" }} data-testid="dm-empty">No conversations.</p>
            : threads.map((t) => (
              <button key={t.thread_id} onClick={() => openThread(t.subscriber_id)} data-testid={`dm-thread-${t.subscriber_id}`}
                className="w-full text-left flex items-center gap-2.5 px-3 py-3 border-b" style={{ borderColor: "var(--border-color)", background: active?.subscriber?.id === t.subscriber_id ? "color-mix(in srgb, var(--accent-primary) 8%, transparent)" : "transparent" }}>
                <Avatar src={t.avatar_url} alt={t.display_name || t.username || ""} size={36} ring={t.rank_ring} testid={`dm-thread-avatar-${t.subscriber_id}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate" style={{ color: "var(--text-primary)" }}>{t.display_name || t.username}</p>
                  <p className="text-xs truncate" style={{ color: "var(--text-secondary)" }}>{t.preview}</p>
                  {!t.is_don && (
                    <span data-testid={`dm-thread-lapsed-${t.subscriber_id}`} className="inline-block mt-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: "var(--bg-subtle)", color: "var(--text-secondary)" }}>No longer Don</span>
                  )}
                </div>
                {t.unread_count > 0 && <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: ACCENT }} />}
              </button>
            ))}
        </div>
      </div>

      {/* Conversation: hidden on mobile until a thread is picked, then full-width */}
      <div className={`${active ? "flex" : "hidden md:flex"} flex-1 flex-col rounded-2xl overflow-hidden`} style={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)" }}>
        {!active ? (
          <div className="flex-1 flex items-center justify-center"><p className="text-sm" style={{ color: "var(--text-secondary)" }}>Select a conversation.</p></div>
        ) : (
          <>
            <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--border-color)" }}>
              <div className="flex items-center gap-2.5">
                <button onClick={() => setActive(null)} data-testid="dm-back" aria-label="Back to conversations" className="md:hidden p-1 -ml-1" style={{ color: "var(--text-primary)" }}><ArrowLeft size={20} /></button>
                <Avatar src={active.subscriber.avatar_url} alt={active.subscriber.display_name || active.subscriber.username || ""} size={32} ring={active.subscriber.rank_ring} testid="dm-active-avatar" />
                <div>
                  <p className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>{active.subscriber.display_name || active.subscriber.username}</p>
                  <p className="text-[11px]" style={{ color: "var(--text-secondary)" }}>@{active.subscriber.username} · {active.subscriber.tier === "don" ? "Don" : "No longer Don"}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={archive} data-testid="dm-archive" title="Archive" className="p-2 rounded-full" style={{ color: "var(--text-secondary)" }}><Archive size={16} /></button>
                <button onClick={toggleBlock} data-testid="dm-block" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: active.is_blocked ? "var(--bg-subtle)" : "#fee2e2", color: active.is_blocked ? "var(--text-secondary)" : "#dc2626" }}>
                  {active.is_blocked ? <><ShieldCheck size={14} /> Unblock</> : <><Ban size={14} /> Block</>}
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {active.messages.map((m) => {
                const fromSamtwo = m.sender_role === "samtwo";
                return (
                  <div key={m.id} className={`flex ${fromSamtwo ? "justify-end" : "justify-start"}`}>
                    <div className="max-w-[75%] px-4 py-2.5 rounded-2xl" style={{ background: fromSamtwo ? ACCENT : "var(--bg-subtle)", color: fromSamtwo ? "#fff" : "var(--text-primary)" }}>
                      <p className="text-sm whitespace-pre-wrap break-words">{tr[m.id]?.show ? tr[m.id].text : m.body}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-[10px]" style={{ color: fromSamtwo ? "rgba(255,255,255,0.7)" : "var(--text-secondary)" }}>{timeStr(m.created_at)}</p>
                        {!fromSamtwo && (
                          <button onClick={() => toggleTranslate(m)} disabled={tr[m.id]?.loading} data-testid={`dm-translate-${m.id}`}
                            className="text-[10px] font-semibold underline underline-offset-2" style={{ color: "var(--accent-primary)" }}>
                            {tr[m.id]?.loading ? "…" : (tr[m.id]?.show ? "Show original" : "Translate")}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
            <div className="border-t px-3 py-3" style={{ borderColor: "var(--border-color)" }}>
              {active.subscriber.tier !== "don" && (
                <p data-testid="dm-lapsed-note" className="text-[11px] mb-2 px-1" style={{ color: "var(--text-secondary)" }}>
                  This subscriber is no longer Don and can't read messages right now. Your reply is saved and waits, unread, until they return to Don.
                </p>
              )}
              <div className="flex items-end gap-2">
                <textarea value={reply} onChange={(e) => setReply(e.target.value.slice(0, MAX))}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  rows={1} placeholder={active.is_blocked ? "This subscriber is blocked" : "Reply as SAMTWO"} disabled={active.is_blocked} data-testid="dm-reply-input"
                  className="flex-1 px-4 py-2.5 rounded-2xl text-sm outline-none resize-none" style={{ background: "var(--bg-subtle)", color: "var(--text-primary)", opacity: active.is_blocked ? 0.6 : 1 }} />
                <button onClick={send} disabled={sending || !reply.trim() || active.is_blocked} data-testid="dm-reply-send"
                  className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: ACCENT, color: "#fff", opacity: sending || !reply.trim() || active.is_blocked ? 0.6 : 1 }}>
                  {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                </button>
              </div>
              <p className="text-[11px] text-right mt-1" style={{ color: "var(--text-secondary)" }}>{reply.length}/{MAX}</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DmInbox;
